import { useMemo, FC, useState, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from 'react-query';
import { IPunchlistAssignToLookup } from '../../../models';
import { getPunchlistAssignToLookup } from '../../../fetch';

export interface ISubscriberOption {
  id: string | number;
  accountName: string | null;
  displayName: string;
  personId: number | null;
  partnerId: number | null;
}

interface IPunchlistItemAssignedTo {
  sageJobId?: number;
  territoryId?: number;
  handleAssignToChange?: () => void;
  isEnabled?: boolean;
  selectedAssignee: IPunchlistAssignToLookup | null;
  setSelectedAssignee: (val: IPunchlistAssignToLookup | null) => void;
}

export const PunchlistItemAssignedTo: FC<IPunchlistItemAssignedTo> = ({
  territoryId,
  handleAssignToChange,
  isEnabled,
  selectedAssignee,
  setSelectedAssignee,
  sageJobId
}) => {
  const [assignToInput, setAssignToInput] = useState('');
  const [selectedInternalAssignee, setInternalSelectedAssignee] =
    useState<IPunchlistAssignToLookup | null>(
      selectedAssignee?.displayName ? selectedAssignee : null
    );

  useEffect(() => {
    setInternalSelectedAssignee(selectedAssignee?.displayName ? selectedAssignee : null);
  }, [selectedAssignee]);

  const { isLoading: isLoadingAssignToItems, data: assignToData } = useQuery<
    IPunchlistAssignToLookup[],
    Error
  >(
    ['assign-to-dropdown-fw', territoryId, sageJobId],
    async () =>
      await getPunchlistAssignToLookup({
        territoryId,
        sageJobId
      }),
    {
      enabled: isEnabled && !!territoryId && !!sageJobId
    }
  );

  const assignToItems: IPunchlistAssignToLookup[] = useMemo(
    () =>
      assignToData?.map(item =>
        item?.isInternalUser ? { ...item, id: item?.accountName } : item
      ) ?? [],
    [assignToData]
  );

  useEffect(() => {
    if (assignToItems?.length > 0 && selectedAssignee && !selectedInternalAssignee) {
      const selected = assignToItems.find(
        result => result.accountName === selectedAssignee?.accountName
      );
      setInternalSelectedAssignee(selected ?? null);
    }
  }, [assignToItems, setInternalSelectedAssignee, selectedAssignee, selectedInternalAssignee]);
  return (
    <Autocomplete
      disabled={isLoadingAssignToItems}
      id="assign-to"
      value={selectedInternalAssignee}
      onChange={(event, newValue: IPunchlistAssignToLookup) => {
        handleAssignToChange && handleAssignToChange();
        setSelectedAssignee(newValue);
        setInternalSelectedAssignee(newValue);
      }}
      inputValue={assignToInput}
      onInputChange={(event, newInputValue) => {
        setAssignToInput(newInputValue);
      }}
      options={assignToItems}
      getOptionLabel={(option: any) => option?.displayName ?? option}
      filterSelectedOptions
      renderOption={(props, option: IPunchlistAssignToLookup) => {
        return (
          <li {...props} key={`test${option.id}`}>
            {option.displayName}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          disabled={isLoadingAssignToItems}
          label={isLoadingAssignToItems ? 'Loading...' : 'Assign To'}
          required
          size="small"
        />
      )}
    />
  );
};
